import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { FileSignature, HardHat, Inbox, RefreshCw } from 'lucide-react';
import { Topbar } from '@/components/layout/Topbar';
import { mainOeuvreApi, LIBELLE_MODE } from '@/api/mainOeuvre';
import { formatFCFA } from '@/utils/currency';
import { OngletTarifs, OngletTaux, OngletEquipes } from './OngletsCoutsMainOeuvre';
import { OngletSemaines } from './OngletSemaines';
import { useAuthStore } from '@/stores/authStore';
import { EngagementJournalierDialog } from '@/components/hr/EngagementJournalierDialog';

/**
 * Main-d'œuvre, côté bureau.
 *
 * 30/09/2026 — refonte sur la logique du PDG : le chef d'équipe ouvre sa
 * journée le matin (qui est là, équipe ou journalier), la clôture le soir (ce
 * qui a été produit, le montant se calcule seul). En fin de semaine, le DAF
 * valide le point de chaque équipe et la caisse paie en une fois.
 *
 * L'onglet « À régler » journée par journée du 29/09 disparaît : ce circuit
 * n'a jamais abouti (aucune journée n'a pu être soumise) et la paie est
 * désormais hebdomadaire.
 *
 * Deux groupes d'onglets : le suivi d'abord, les réglages derrière un
 * séparateur — ce qu'on vient faire chaque semaine ne se mélange pas avec ce
 * qu'on règle une fois.
 */
type Onglet = 'semaines' | 'registre' | 'reglements' | 'tarifs' | 'taux' | 'equipes';

export const MainOeuvrePage = () => {
  // La holding ne fabrique rien : ses ouvriers et ses journées appartiennent
  // aux filiales. Un tableau vide sans explication se lit comme une panne.
  const estHolding = useAuthStore((s) => s.filiale?.metier) === 'generic';
  const [onglet, setOnglet] = useState<Onglet>('semaines');
  const qc = useQueryClient();
  // CONTRATS (20/10/2026) : engagement écrit du journalier, émis par la RH ou le DAF.
  const role = useAuthStore((s) => s.user?.role);
  const peutEngager = role === 'rh' || role === 'daf' || role === 'admin_holding';
  const [engage, setEngage] = useState<{ id: string; nom: string; telephone: string; metier: string | null } | null>(null);

  const registreQ = useQuery({ queryKey: ['main-oeuvre', 'ouvriers'],   queryFn: mainOeuvreApi.ouvriers,          staleTime: 60_000, enabled: onglet === 'registre' });
  const payesQ    = useQuery({ queryKey: ['main-oeuvre', 'reglements'], queryFn: mainOeuvreApi.reglementsSemaine, staleTime: 60_000, enabled: onglet === 'reglements' });

  return (
    <>
      <Topbar
        title="Main-d'œuvre"
        subtitle="Journées de chantier, point de la semaine, paie"
        actions={
          <button className="ds-btn ds-btn-g ds-btn-sm" onClick={() => qc.invalidateQueries({ queryKey: ['main-oeuvre'] })}>
            <RefreshCw size={14} /> Rafraîchir
          </button>
        }
      />

      <div style={{ padding: '16px 20px 40px' }}>
        <div className="ds-alert-strip ds-alert-i" style={{ marginBottom: 14 }}>
          <span className="ds-alert-ico"><HardHat size={20} /></span>
          <div className="ds-alert-txt">
            Le matin, chaque chef d'équipe ouvre sa journée sur le portail terrain ; le soir, il saisit la
            production et le montant se calcule sur les tarifs ci-dessous. En fin de semaine, le DAF valide
            le point de l'équipe, puis la caisse paie <strong>ici seulement</strong> — l'écriture comptable
            naît au paiement.
          </div>
        </div>

        <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap', alignItems: 'center' }}>
          {([
            ['semaines',   'Point de la semaine'],
            ['registre',   'Registre des ouvriers'],
            ['reglements', 'Paies passées'],
            ['separateur', ''],
            ['tarifs',     'Tarifs par produit'],
            ['taux',       'Taux journaliers'],
            ['equipes',    'Équipes'],
          ] as const).map(([cle, libelle]) => cle === 'separateur' ? (
            <span key="separateur" aria-hidden="true" style={{ width: 1, height: 24, background: 'var(--bd-2, #D1D5DB)', margin: '0 4px' }} />
          ) : (
            <button
              key={cle}
              onClick={() => setOnglet(cle as Onglet)}
              className={`ds-btn ds-btn-sm ${onglet === cle ? 'ds-btn-p' : 'ds-btn-g'}`}
            >{libelle}</button>
          ))}
        </div>

        {onglet === 'semaines' && <OngletSemaines estHolding={estHolding} />}
        {engage && <EngagementJournalierDialog open onClose={() => setEngage(null)} ouvrier={engage} />}

        {onglet === 'registre' && (
          <div className="ds-table-panel">
            <div className="ds-table-header">
              <div>
                <div className="ds-table-title">Registre des ouvriers</div>
                <div className="ds-table-sub">
                  Le téléphone identifie l'ouvrier — un homme revenu est reconnu, son historique s'accumule
                </div>
              </div>
            </div>
            <table className="ds-tbl">
              <thead>
                <tr>
                  <th>Ouvrier</th>
                  <th style={{ width: 130 }}>Métier</th>
                  <th style={{ width: 110 }}>Journées</th>
                  <th style={{ width: 130 }}>Déjà payé</th>
                  <th style={{ width: 130 }}>Reste dû</th>
                  <th style={{ width: 110 }}>Photo</th>
                  {peutEngager && <th style={{ width: 130 }}>Engagement</th>}
                </tr>
              </thead>
              <tbody>
                {registreQ.isLoading && <tr><td colSpan={peutEngager ? 7 : 6} style={{ textAlign: 'center', padding: 32 }}>Chargement…</td></tr>}
                {!registreQ.isLoading && (registreQ.data ?? []).length === 0 && (
                  <tr><td colSpan={peutEngager ? 7 : 6} style={{ textAlign: 'center', padding: 48 }}>
                    <Inbox size={28} style={{ margin: '0 auto 8px', color: 'var(--tx-3)' }} />
                    <p style={{ fontSize: 13, color: 'var(--tx-3)' }}>
                      {estHolding
                        ? "SIM GROUP ne recrute pas d'ouvriers : ils appartiennent aux filiales. Ouvrez Leadbeton CI depuis Holding, en « Vue PDG »."
                        : "Aucun ouvrier enregistré. Les chefs d'équipe les ajoutent depuis le portail terrain, le matin."}
                    </p>
                  </td></tr>
                )}
                {(registreQ.data ?? []).map((o) => (
                  <tr key={o.id}>
                    <td>
                      <div style={{ fontWeight: 600 }}>{o.nom}</div>
                      <div style={{ fontSize: 11, color: 'var(--tx-3)' }}>{o.telephone}</div>
                    </td>
                    <td>{o.metier ?? '—'}</td>
                    <td className="ds-am">{o.joursCumules.toLocaleString('fr-FR')}</td>
                    <td className="ds-am">{formatFCFA(o.montantPayeCents)}</td>
                    <td className="ds-am" style={{ fontWeight: Number(o.montantDuCents) > 0 ? 700 : 400, color: Number(o.montantDuCents) > 0 ? 'var(--warning)' : undefined }}>
                      {formatFCFA(o.montantDuCents)}
                    </td>
                    <td>
                      {o.photoUrl
                        ? <img src={o.photoUrl} alt="" style={{ width: 34, height: 34, borderRadius: 17, objectFit: 'cover' }} />
                        : <span style={{ fontSize: 11, color: 'var(--tx-3)' }}>sans photo</span>}
                    </td>
                    {peutEngager && (
                      <td>
                        <button className="ds-btn ds-btn-g ds-btn-sm" onClick={() => setEngage({ id: o.id, nom: o.nom, telephone: o.telephone, metier: o.metier ?? null })}>
                          <FileSignature size={12} /> Engagement
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {onglet === 'reglements' && (
          <div className="ds-table-panel">
            <div className="ds-table-header">
              <div>
                <div className="ds-table-title">Paies passées</div>
                <div className="ds-table-sub">Un passage en caisse = une écriture, une liste émargée</div>
              </div>
            </div>
            <table className="ds-tbl">
              <thead>
                <tr>
                  <th style={{ width: 110 }}>Payé le</th>
                  <th>Ouvrier</th>
                  <th style={{ width: 120 }}>Semaine du</th>
                  <th style={{ width: 140 }}>Chef d'équipe</th>
                  <th style={{ width: 130 }}>Montant</th>
                  <th style={{ width: 120 }}>Mode</th>
                  <th>Référence</th>
                </tr>
              </thead>
              <tbody>
                {payesQ.isLoading && <tr><td colSpan={7} style={{ textAlign: 'center', padding: 32 }}>Chargement…</td></tr>}
                {!payesQ.isLoading && (payesQ.data ?? []).length === 0 && (
                  <tr><td colSpan={7} style={{ textAlign: 'center', padding: 48 }}>
                    <Inbox size={28} style={{ margin: '0 auto 8px', color: 'var(--tx-3)' }} />
                    <p style={{ fontSize: 13, color: 'var(--tx-3)' }}>Aucune paie enregistrée à ce jour.</p>
                  </td></tr>
                )}
                {(payesQ.data ?? []).map((p) => (
                  <tr key={p.id}>
                    <td style={{ whiteSpace: 'nowrap' }}>{p.payeLe.slice(0, 10)}</td>
                    <td>
                      <div style={{ fontWeight: 600 }}>{p.ouvrier.nom}</div>
                      <div style={{ fontSize: 11, color: 'var(--tx-3)' }}>{p.ouvrier.telephone}</div>
                    </td>
                    <td>{p.semaine}</td>
                    <td>{p.chef}</td>
                    <td className="ds-am" style={{ fontWeight: 700 }}>{formatFCFA(p.montantCents)}</td>
                    <td><span className="ds-bd ds-bd-n" style={{ fontSize: 10 }}>{LIBELLE_MODE[p.mode]}</span></td>
                    <td style={{ fontSize: 12, color: 'var(--tx-3)' }}>
                      {p.reference ?? '—'}
                      {p.preuveUrl && (
                        <a href={p.preuveUrl} target="_blank" rel="noreferrer"
                           style={{ display: 'block', marginTop: 3, color: 'var(--primary)', fontSize: 11 }}>
                          Voir la liste émargée
                        </a>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Les réglages. Chaque onglet charge ses propres données. */}
        {onglet === 'tarifs'  && <OngletTarifs />}
        {onglet === 'taux'    && <OngletTaux />}
        {onglet === 'equipes' && <OngletEquipes />}
      </div>
    </>
  );
};
